import { FC, useMemo } from 'react';
import { Box, Typography } from '@oresundsbron/bridge-ui';
import { cx } from 'class-variance-authority';
import { ChevronRight } from '@oresundsbron/icons';
import { DefaultLink } from '~/components/Links/DefaultLink';
import { useLinksV2 } from '~/hooks/useLinksV2';

export const Breadcrumbs: FC<{
  id: string;
  invert?: boolean;
  className?: string;
  addRight?: { path: string; title: string }[];
  addLeft?: { path: string; title: string }[];
}> = ({ id, invert, className }) => {
  const { getLink, findLink } = useLinksV2();

  const crumbs = useMemo(() => {
    const link = getLink(id);
    if (!link || !link.slugs || link.slugs.length === 0) return undefined;

    const breadcrumbLinks = link.slugs.reduce((acc: typeof link[], _, i) => {
      const slugPath = `/${link.slugs.slice(0, i + 1).join('/')}`;
      const found = findLink(slugPath);
      if (found && !found.skip) acc.push(found);
      return acc;
    }, []);

    if (breadcrumbLinks.length <= 1) return undefined;

    return breadcrumbLinks.map(({ path, title }) => ({ path, title }));
  }, [id, getLink, findLink]);

  if (!crumbs) return null;

  return (
    <Box
      as="ul"
      id="breadcrumbs"
      itemScope
      itemType="https://schema.org/BreadcrumbList"
      className={cx(' flex flex-wrap gap-1 pl-4 text-sm md:pl-16', className)}
    >
      {crumbs.map(({ path, title }, i) => (
        <Box
          key={path}
          as="li"
          className="flex items-center gap-1"
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          {i !== crumbs.length - 1 ? (
            <DefaultLink
              itemProp="item"
              itemType="https://schema.org/WebPage"
              href={path}
              color={invert ? 'inherit' : 'neutral'}
              className={cx(
                'hover:no-underline',
                invert ? 'text-white' : undefined,
                'underline'
              )}
            >
              <span itemProp="name">{title}</span>
            </DefaultLink>
          ) : (
            <Typography
              as="span"
              size="md"
              className={cx(invert ? 'text-neutral-400' : 'text-neutral-500')}
            >
              <span itemProp="name">{title}</span>
            </Typography>
          )}
          {i !== crumbs.length - 1 && (
            <Box
              as="span"
              aria-hidden
              className={cx(invert ? 'text-neutral-400' : 'text-neutral-500')}
            >
              <ChevronRight className="size-4 text-type-secondary" />
            </Box>
          )}
          <meta itemProp="position" content={(i + 1).toString()} />
        </Box>
      ))}
    </Box>
  );
};
